
/**
 * Weakness Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { WeaknessFormSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class WeaknessService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                categories: body.categories || [],
                riskLevels: body.riskLevels || [],
                message: body.message || 'Weakness listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch weakness list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch weakness');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {

        // Rule: Validation at boundary
        const validation = WeaknessFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;


        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Weakness oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create weakness');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {

        // Validate before update
        const validation = WeaknessFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;


        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Weakness güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update weakness');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Weakness silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete weakness');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            // Use optimized batch delete endpoint
            const response = await this.api.client.post(`${this.api.basePath}/batch-delete`, { ids });

            return {
                success: true,
                data: response.data,
                message: response.data.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            // Fallback to sequential delete if batch endpoint fails
            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} öğe silindi`
            };
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
